import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Star, ExternalLink, TrendingUp, AlertTriangle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import RealTimeStatusBadge from '../../components/ui/RealTimeStatusBadge';
import Button from '../../components/ui/Button';
import ProviderOverview from './components/ProviderOverview';
import OutageHistory from './components/OutageHistory';
import ReliabilityMetrics from './components/ReliabilityMetrics';
import UserReviews from './components/UserReviews';
import SubscriptionControl from './components/SubscriptionControl';
import Icon from '../../components/AppIcon';


const ServiceProviderProfile = () => {
  const [searchParams] = useSearchParams();
  const providerId = searchParams?.get('id');
  
  const [provider, setProvider] = useState(null);
  const [outages, setOutages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (providerId) {
      fetchProviderData();
    }
  }, [providerId]);

  const fetchProviderData = async () => {
    setLoading(true);
    try {
      // Fetch provider details
      const { data: providerData, error: providerError } = await supabase
        ?.from('service_providers')
        ?.select('*')
        ?.eq('id', providerId)
        ?.single();

      if (providerError) throw providerError;

      // Fetch recent outages for this provider
      const { data: outageData, error: outageError } = await supabase
        ?.from('service_outages')
        ?.select(`
          *,
          service_providers!inner(name, category),
          user_profiles!inner(full_name)
        `)
        ?.eq('provider_id', providerId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (outageError) throw outageError;

      setProvider(providerData);
      setOutages(outageData || []);
    } catch (err) {
      console.error('Error fetching provider data:', err);
      setError('Failed to load provider information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Provider Not Found</h2>
          <p className="text-gray-600">{error || 'The service provider you\'re looking for could not be found.'}</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'history', label: 'Outage History', icon: Clock },
    { id: 'reviews', label: 'User Reviews', icon: Star },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Provider Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              {provider?.logo_url && (
                <img
                  src={provider?.logo_url}
                  alt={`${provider?.name} logo`}
                  className="h-16 w-16 rounded-lg object-contain bg-gray-100 p-2"
                />
              )}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{provider?.name}</h1>
                <div className="flex items-center space-x-4 mt-2">
                  <RealTimeStatusBadge status={provider?.status} />
                  <span className="text-sm text-gray-500 capitalize">
                    {provider?.category?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <SubscriptionControl providerId={provider?.id} />
              {provider?.website_url && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(provider?.website_url, '_blank')}
                  className="flex items-center"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Visit Website
                </Button>
              )}
            </div>
          </div>
          
          {provider?.description && (
            <p className="text-gray-600 mt-4 leading-relaxed">{provider?.description}</p>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs?.map((tab) => {
                const Icon = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'overview' && (
            <>
              <ReliabilityMetrics provider={provider} outages={outages} />
              <ProviderOverview provider={provider} recentOutages={outages?.slice(0, 5)} />
            </>
          )}
          
          {activeTab === 'history' && (
            <OutageHistory provider={provider} outages={outages} />
          )}
          
          {activeTab === 'reviews' && (
            <UserReviews providerId={provider?.id} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceProviderProfile;